import { useEffect, useRef, useState } from "react"
import { Link } from "@tanstack/react-router"
import { format } from "date-fns"
import { IconUserCheckFill18 as InvitedIcon } from "nucleo-ui-fill-18"
import {
	IconStackOutline18 as AllIcon,
	IconChevronDownOutline18 as ChevronDown,
	IconChevronRightOutline18 as ChevronRight,
	IconXmarkOutline18 as DismissIcon,
	IconSquareArrowUpRightOutline18 as ExternalLink,
	IconUserPlusOutline18 as InviteIcon,
	IconCircleDashedOutline18 as OpenIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Avatar,
	Badge,
	Button,
	IconButton,
	Input,
	SegmentedControl,
	Tooltip,
	Virtualizer,
} from "@productlane/ui"

import type { RouterOutputs } from "@/lib/api"
import { ReportingChartFrame } from "@/components/reporting/charts"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import {
	canReview,
	isOpen,
	linkedinSlug,
	statusView,
} from "./gtm-signup-checkins-view"

type Row = RouterOutputs["analytics"]["signupCheckins"]["rows"][number]
type ListView = "open" | "all"

const VIEW_OPTIONS = [
	{ value: "open", label: "Open", Icon: OpenIcon },
	{ value: "all", label: "All", Icon: AllIcon },
] as const

// A rejected URL, a decided row or missing lemlist setup is guidance, not a failure.
const GUIDANCE_CODES = ["BAD_REQUEST", "PRECONDITION_FAILED", "NOT_FOUND"]

function showError(
	error: { message: string; data?: { code?: string } | null },
	icon: typeof InviteIcon,
) {
	const show = GUIDANCE_CODES.includes(error.data?.code ?? "")
		? toast.info
		: toast.error
	show({ title: error.message, icon })
}

function TruncatedText({
	text,
	className,
}: {
	text: string
	className?: string
}) {
	const ref = useRef<HTMLDivElement>(null)
	const [isTruncated, setIsTruncated] = useState(false)

	useEffect(() => {
		const element = ref.current
		if (!element) return
		const measure = () =>
			setIsTruncated(element.scrollWidth > element.clientWidth)
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(element)
		return () => observer.disconnect()
	}, [text])

	// Measure a wrapper that never remounts; Tooltip swaps its own child when enabled flips.
	return (
		<div ref={ref} className={cn("truncate", className)}>
			<Tooltip label={text} enabled={isTruncated}>
				<span>{text}</span>
			</Tooltip>
		</div>
	)
}

const openProfile = (url: string) =>
	window.open(url, "_blank", "noopener,noreferrer")

export function GtmSignupCheckins() {
	const query = api.analytics.signupCheckins.useQuery(
		{},
		{ staleTime: 30_000, keepPreviousData: true },
	)
	const allRows = query.data?.rows ?? []
	const [view, setView] = useState<ListView>("open")
	const rows = view === "open" ? allRows.filter(isOpen) : allRows
	const reviewCount = allRows.filter((row) => row.status === "REVIEW").length
	const [expandedId, setExpandedId] = useState<string | null>(null)

	return (
		<ReportingChartFrame
			className={cn(query.isFetching && !query.isLoading && "opacity-60")}
			title="Signup check-ins"
			description="LinkedIn check-in for first-time signups, one day after the workspace is created. Last 30 days."
			badge={
				reviewCount > 0 ? (
					<Badge variant="important" size="sm">
						{reviewCount} to review
					</Badge>
				) : undefined
			}
			isLoading={query.isLoading}
			isEmpty={!query.isLoading && rows.length === 0}
			emptyMessage={
				query.isError
					? `Couldn't load the check-ins: ${query.error.message}`
					: allRows.length > 0
						? "Nothing open. Switch to All to see the history."
						: "No signups yet"
			}
			legend={
				<SegmentedControl
					name="signup-checkins-view"
					value={view}
					options={VIEW_OPTIONS}
					onValueChange={setView}
					showLabels
				/>
			}
		>
			<div className="overflow-x-auto">
				<div className="min-w-[620px] text-sm">
					<div className="flex items-center gap-3 border-b border-gray-200 px-2 pb-2 text-xs text-tertiary dark:border-gray-700/80">
						<div className="w-6 flex-none" />
						<div className="min-w-36 flex-1">Person</div>
						<div className="w-28 flex-none">Company</div>
						<div className="w-24 flex-none">Signed up</div>
						<div className="w-20 flex-none">Status</div>
						<div className="min-w-0 flex-1">Reason</div>
						<div className="w-8 flex-none" />
					</div>

					<Virtualizer
						key={view}
						className="max-h-[calc(100dvh-14rem)] pb-2"
						estimateSize={44}
						itemsData={rows}
						itemContent={(row) => (
							<CheckinRow
								row={row}
								isExpanded={expandedId === row.id}
								onToggle={() =>
									setExpandedId((current) =>
										current === row.id ? null : row.id,
									)
								}
							/>
						)}
					/>
				</div>
			</div>
		</ReportingChartFrame>
	)
}

function CheckinRow({
	row,
	isExpanded,
	onToggle,
}: {
	row: Row
	isExpanded: boolean
	onToggle: () => void
}) {
	const view = statusView(row.status)
	const reviewable = canReview(row)
	const expandable = reviewable || row.candidates.length > 0

	return (
		<div>
			<div
				className="my-0.5 flex items-center gap-3 rounded-md px-2 py-2 hover:bg-gray-100 dark:hover:bg-gray-850/50"
				role="presentation"
				onClick={expandable ? onToggle : undefined}
			>
				<div className="w-6 flex-none">
					{expandable ? (
						<IconButton
							variant="ghost"
							size="md"
							Icon={isExpanded ? ChevronDown : ChevronRight}
							aria-label={isExpanded ? "Collapse" : "Expand"}
							onClick={(event) => {
								event.stopPropagation()
								onToggle()
							}}
						/>
					) : null}
				</div>
				<div className="flex min-w-36 flex-1 items-center gap-2">
					<Avatar
						size="xs"
						name={row.name || row.email}
						image={row.imageUrl ?? undefined}
					/>
					<div className="min-w-0">
						<TruncatedText text={row.name || row.email} />
						<TruncatedText text={row.email} className="text-xs text-tertiary" />
					</div>
				</div>
				<Link
					to="/iluvatar/workspaces/$id"
					params={{ id: row.workspaceId }}
					onClick={(event) => event.stopPropagation()}
					className="w-28 flex-none text-secondary hover:underline"
				>
					<TruncatedText text={row.company} />
				</Link>
				<div className="w-24 flex-none text-tertiary">
					{format(new Date(row.signedUpAt), "MMM d, yyyy")}
				</div>
				<div className="w-20 flex-none">
					<Tooltip
						label={
							row.decidedBy
								? `${view.label} by ${row.decidedBy}`
								: `${view.label} · ${row.language.toUpperCase()}`
						}
					>
						<Badge variant={view.badge} size="sm">
							{view.label}
						</Badge>
					</Tooltip>
				</div>
				<TruncatedText
					text={row.reason ?? ""}
					className="min-w-0 flex-1 text-secondary"
				/>
				<div className="w-8 flex-none">
					{row.linkedinUrl ? (
						<Tooltip label="Open LinkedIn profile">
							<IconButton
								variant="ghost"
								size="md"
								Icon={ExternalLink}
								aria-label="Open LinkedIn profile"
								onClick={(event) => {
									event.stopPropagation()
									if (row.linkedinUrl) openProfile(row.linkedinUrl)
								}}
							/>
						</Tooltip>
					) : null}
				</div>
			</div>
			{isExpanded ? <CheckinReview row={row} reviewable={reviewable} /> : null}
		</div>
	)
}

function CheckinReview({ row, reviewable }: { row: Row; reviewable: boolean }) {
	const utils = api.useUtils()
	const [pastedUrl, setPastedUrl] = useState("")
	const refresh = () => utils.analytics.signupCheckins.invalidate()

	const invite = api.analytics.inviteSignupCheckin.useMutation({
		onSuccess: async () => {
			toast.success({
				title: `${row.name || row.email} gets the LinkedIn invite`,
				icon: InvitedIcon,
			})
			setPastedUrl("")
			await refresh()
		},
		onError: (error) => showError(error, InviteIcon),
	})
	const dismiss = api.analytics.dismissSignupCheckin.useMutation({
		onSuccess: async () => {
			toast.success({
				title: `${row.name || row.email} closed without an invite`,
				icon: DismissIcon,
			})
			await refresh()
		},
		onError: (error) => showError(error, DismissIcon),
	})
	const busy = invite.isLoading || dismiss.isLoading
	const sendInvite = (linkedinUrl: string) => {
		invite.mutate({ id: row.id, linkedinUrl })
	}

	return (
		<div className="mb-2 ml-11 flex flex-col gap-2 rounded-md bg-gray-50 p-3 dark:bg-gray-850/50">
			{reviewable && row.candidates.length > 0 ? (
				<div className="text-xs text-tertiary">
					Compare each profile with the signup&apos;s email and company, then
					invite the right person.
				</div>
			) : null}
			{row.candidates.length === 0 ? (
				<div className="text-xs text-tertiary">
					No candidate found. Paste the right profile below.
				</div>
			) : (
				row.candidates.map((candidate) => (
					<div key={candidate.linkedinUrl} className="flex items-center gap-3">
						<div className="min-w-0 flex-1">
							<TruncatedText
								text={candidate.fullName ?? linkedinSlug(candidate.linkedinUrl)}
							/>
							<TruncatedText
								text={[
									candidate.company,
									candidate.country,
									candidate.potentialEmail,
								]
									.filter(Boolean)
									.join(" · ")}
								className="text-xs text-tertiary"
							/>
						</div>
						<IconButton
							variant="ghost"
							size="md"
							Icon={ExternalLink}
							aria-label="Open LinkedIn profile"
							onClick={() => openProfile(candidate.linkedinUrl)}
						/>
						{reviewable ? (
							<Button
								variant="outline"
								size="sm"
								Icon={InviteIcon}
								disabled={busy}
								isLoading={
									invite.isLoading &&
									invite.variables?.linkedinUrl === candidate.linkedinUrl
								}
								onClick={() => sendInvite(candidate.linkedinUrl)}
							>
								Invite
							</Button>
						) : null}
					</div>
				))
			)}

			{reviewable ? (
				<div className="flex items-center gap-2 border-t border-gray-200 pt-2 dark:border-gray-700/80">
					<Input
						name={`signup-checkin-url-${row.id}`}
						textSize="sm"
						placeholder="https://www.linkedin.com/in/..."
						value={pastedUrl}
						className="flex-1"
						inputClassName="h-7"
						onChange={(event) => setPastedUrl(event.target.value)}
					/>
					<Button
						variant="outline"
						size="sm"
						Icon={InviteIcon}
						disabled={busy || pastedUrl.trim().length === 0}
						isLoading={
							invite.isLoading &&
							invite.variables?.linkedinUrl === pastedUrl.trim()
						}
						onClick={() => sendInvite(pastedUrl.trim())}
					>
						Invite
					</Button>
					<Button
						variant="ghost"
						size="sm"
						Icon={DismissIcon}
						disabled={busy}
						isLoading={dismiss.isLoading}
						onClick={() => dismiss.mutate({ id: row.id })}
					>
						Dismiss
					</Button>
				</div>
			) : null}
		</div>
	)
}
